<template>
  <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
    <div>
      <RecruitmentSmallRecap :recruitment="recruitment" @open-recruitment-recap="openRecruitmentRecap" />

      <transition-group tag="div" name="candidate-list">
        <CandidateTemporaryCard
          v-for="candidate in candidates"
          :id="candidate.getId()"
          :key="candidate.getId()"
          :candidate="candidate"
          :recruitment="recruitment"
          :show-experience="true"
          :can-ignore="true"
          :can-contact="false"
          @ignore-candidate="ignoreCandidate"
          @go-to-profile="goToProfile"
        />
      </transition-group>

      <NoCandidate
        v-if="!recruitment.isExpired() && candidates.length === 0"
        :img="placeholderImg"
        :title="$t('ETAB.PAGES.CANDIDATE.@NO_CURRENT_CANDIDATES')"
      />

      <ExpiredRecruitment v-if="recruitment.isExpired()" />
    </div>
  </transition>
</template>

<script setup>
  import { GETTER_RECRUITMENT_GET_CURRENT, ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL, ACTION_RECRUITMENT_GET } from "@etab/stores/types";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useDashboardStore } from "@etab/stores/dashboard";
  import { ETAB_ROUTES } from "@etab/config";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { computed, onMounted } from "vue";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { useRecruitmentCandidate } from "@etab/composables";
  import { useTransition } from "@core/composables";
  import { useRoute, useRouter } from "vue-router";

  import CandidateTemporaryCard from "@etab/components/candidates/CandidateTemporary.card.vue";
  import NoCandidate from "@etab/components/candidates/NoCandidate.functional.vue";
  import ExpiredRecruitment from "@etab/components/candidates/ExpiredRecruitment.component.vue";
  import RecruitmentSmallRecap from "@etab/components/recruitments/detail/RecruitmentSmallRecap.component.vue";

  import placeholderImg from "@etab/assets/img/placeholders/candidate_current_placeholder.png";

  const { transition } = useTransition();
  const {
    $_goToProfileFromMultiCandidatesMERDemand,
    $_goToProfileFromTemporaryDemand,
    $_scrollToPreviouslyOpenedExtra,
    $_ignoreAECandidate,
    $_ignoreMERCandidate,
  } = useRecruitmentCandidate();
  const route = useRoute();
  const router = useRouter();

  const dashboardStore = useDashboardStore();
  const recruitmentStore = useRecruitmentStore();

  // Computed
  const demandId = computed(() => {
    return route.params.demandId;
  });

  const demandType = computed(() => {
    return route.params.demandType;
  });

  const recruitment = computed(() => {
    return recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
  });

  const candidates = computed(() => {
    if (recruitment.value.isExpired()) {
      // RG: Recruitment candidates - we don't display candidates for an expired recruitment
      return [];
    }
    return recruitment.value.getCurrentCandidates();
  });

  // Methods
  const openRecruitmentRecap = function () {
    dashboardStore[ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL]({ recap: recruitment.value });
  };

  const goToProfile = function ({ candidate }) {
    $_goToProfileFromTemporaryDemand({ candidate });
  };

  const ignoreCandidate = async function ({ candidate }) {
    try {
      LoadingManager.show();
      if (recruitment.value.isAE()) {
        await $_ignoreAECandidate({ candidate });
      }
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      // stoploading after refresh
      refreshRecruitment();
    }
  };

  const refreshRecruitment = async function () {
    try {
      LoadingManager.show();
      await recruitmentStore[ACTION_RECRUITMENT_GET]({ demandId: demandId.value, demandType: demandType.value });
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  // Created
  if (recruitment.value.isFilled()) {
    // RG: applied candidates tab - a filled recruitment shouldn't see the applied candidates page
    router.replace({
      name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.selected.name,
      params: { demandId: demandId.value, demandType: demandType.value },
    });
  }

  // Mounted
  onMounted(() => {
    $_scrollToPreviouslyOpenedExtra();
  });
</script>
